// Vehicles.tsx
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { currentYear, parseCurrencyToNumber } from "../../utils/schema";
import { ViewContext } from "components/lib";
import { fetchCompanyVehicles } from "./api";
import { VehicleListItem } from "./VehicleListItem";
import { VehicleUpsertDialog } from "./VehicleUpsertDialog";
import { ExistingVehiclesPicker } from "./ExistingVehiclesPicker";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../../../components/ui/dialog";
import { Plus } from "lucide-react";
import { getClass as mapClass } from "./utils";
import { FieldError } from "../FieldError";
import { useEffect } from "react";
import { SwitchInput } from "../SwitchInput";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function Vehicles() {
  const { control, setValue, getValues, watch, register, trigger } = useFormContext();
  const { fields, append, update, remove, replace } = useFieldArray({
    control,
    name: "step6.vehicles",
    keyName: "fieldId",
  });

  const context = React.useContext(ViewContext);
  const location = useLocation();
  const companyId = location?.state?.data?.company_id;
  const quoteId = location?.state?.data?.quote_id;

  const [openChooser, setOpenChooser] = React.useState(false);
  const [openSelect, setOpenSelect] = React.useState(false);
  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [mode, setMode] = React.useState("create");
  const [editingIndex, setEditingIndex] = React.useState(null);

  const [existingVehicles, setExistingVehicles] = React.useState([]);

  // --- Mirror carriers: step1.carrier -> step6.carrier
  const step1Carrier = useWatch({ control, name: "step1.carrier" });
  const step6Carrier = useWatch({ control, name: "step6.carrier" });

  React.useEffect(() => {
    register("step6.carrier"); // hazmat toggle
  }, [register]);

  React.useEffect(() => {
    const list = Array.isArray(step1Carrier) ? step1Carrier : step1Carrier ? [step1Carrier] : [];
    const normalized = list.map((s) => (s ?? ""));
    if (!arraysEqual(normalized, step6Carrier ?? [])) {
      setValue("step6.carrier", normalized, { shouldDirty: false, shouldValidate: true });
    }
  }, [step1Carrier, step6Carrier, setValue]);

  // --- Hazmat toggle
  const hazmat = useWatch({ control, name: "step1.hazmatPlacard" });
  const blockedCls = hazmat ? "opacity-50 pointer-events-none" : "";

  // load vehicles from server for company (unchanged) ...
  React.useEffect(() => {
    if (companyId) {
      fetchCompanyVehicles(companyId)
        .then((rows) => {
          const items = rows.map((v) => ({
            type: v.type ?? "vehicle",
            id: v.id,
            vehicleId: v.id,
            vin: v.vin,
            model: v.model,
            year: v.year,
            make: v.make,
            class: mapClass(v.class),
            bodyType: v.bodytype,
            hitchType: v.hitchType,
            value: String(v.stated_value ?? "").replaceAll(/\D/g, ""),
            deductibleType: "FollowsPolicy",
            deductible: "2500",
            includeAPDTowing: !!v.includeAPDTowing,
          }));
          if (!quoteId) {
            const totalValue = items.reduce((acc, it) => acc + Number(it.value || 0), 0);
            replace(items);
            setValue("step6.nbrOfTrucks", items.length, { shouldDirty: false });
            setValue("step6.valueOfTrucks", totalValue, { shouldDirty: false });
          }
          setExistingVehicles(items);
        })
        .catch((err) => {
          console.error(err);
          context.notification?.show?.("Error on load company vehicles", "error", true);
        });
    }
  }, [companyId, quoteId, replace, setValue, context.notification]);

  // keep totals current (unchanged)
  const vehicles = watch("step6.vehicles");
  React.useEffect(() => {
    const n = vehicles?.length ?? 0;
    const total = (vehicles ?? []).reduce((acc, v) => acc + Number(parseCurrencyToNumber(v.value)), 0);
    setValue("step6.nbrOfTrucks", n, { shouldDirty: false });
    setValue("step6.valueOfTrucks", total, { shouldDirty: false });
  }, [vehicles, setValue]);

  const withAgeMessage = (v) => {
    const age = currentYear - Number(v.year);
    const isDump = v.bodyType === "dump_truck";
    const maxAge = isDump ? 26 : 23;
    const hasCoverWhale = (step6Carrier ?? []).includes("Cover Whale");
    const hasNirvana = (step6Carrier ?? []).includes("Nirvana");
    const hasProgressive = (step6Carrier ?? []).includes("Progressive");

    if (!hasCoverWhale && !hasNirvana) return v; // Progressive-only or other carriers: no age warning

    if (age > maxAge) {
      const message = hasProgressive
        ? `Cover Whale/Nirvana do not accept ${isDump ? "dump trucks" : "vehicles"} above ${maxAge} years of age. Continuing will quote Progressive only.`
        : isDump
          ? "Dump trucks above 26 years of age are not eligible"
          : "Vehicles above 23 years of age are not eligible";

      return { ...v, _ageError: message };
    }

    return v;
  };

  const openCreate = () => { setMode("create"); setEditingIndex(null); setUpsertOpen(true); };
  const openEdit = (idx) => { setMode("edit"); setEditingIndex(idx); setUpsertOpen(true); };
  const onUpsertDone = (vehicle) => { if (mode === "create") append(vehicle); else if (editingIndex != null) update(editingIndex, vehicle); };

  useEffect(() => {
    if (fields.length > 0) {
      trigger("step6.vehicles");
    }
  }, [fields]);

  const hasAccelerant = step6Carrier?.includes("Accelerant");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">Vehicles</h2>
        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setOpenChooser(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Vehicle
        </Button>
      </div>

      {/* Read-only list + Progressive hitch selector per vehicle */}
      <div className={`${blockedCls} space-y-3`}>
        {fields.length > 0 && (
          <div className="space-y-3">
            {fields.map((row, index) => {
              const v = withAgeMessage(vehicles[index]);
              return (
                <div key={row.fieldId} className="space-y-3">
                  <VehicleListItem
                    vehicle={v}
                    index={index}
                    onEdit={() => openEdit(index)}
                    onDelete={() => remove(index)}
                  />
                </div>
              );
            })}
          </div>
        )}
        <FieldError step={"step6"} name="step6.vehicles" />
      </div>

      {/* Chooser */}
      <Dialog open={openChooser} onOpenChange={setOpenChooser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-center">Create a new vehicle or choose from your existing vehicles?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Button variant="outline" onClick={() => { setOpenChooser(false); setOpenSelect(true); }}>
              Select Existing
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setOpenChooser(false); openCreate(); }}>
              Create New Vehicle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Select Existing */}
      <Dialog open={openSelect} onOpenChange={setOpenSelect}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Existing Vehicle</DialogTitle>
          </DialogHeader>
          <ExistingVehiclesPicker
            existing={existingVehicles}
            selected={vehicles ?? []}
            onPick={(v) => {
              // no duplicates by VIN
              const cur = getValues("step6.vehicles") || [];
              if (cur.some((x) => x.vin === v.vin)) {
                context.notification?.show?.("Vehicle already added.", "error", true);
                return;
              }
              append(v);
              setOpenSelect(false);
            }}
          />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit dialog */}
      <VehicleUpsertDialog
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        mode={mode}
        initial={editingIndex != null ? vehicles[editingIndex] : undefined}
        companyId={companyId}
        onDone={onUpsertDone}
      />
      {hasAccelerant && (
        <div className="space-y-2">
          <SwitchInput
            key={'vehicle-files-dot-compliant'}
            control={control}
            id={'vehicle-files-dot-compliant'}
            stepName={'step6.vehicleFilesDotCompliant'}
            stepId="step6"
            label={"Are vehicle files DOT compliant?"}
          />
        </div>
      )}
      
    </div>
  );
}
